import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch, useSelector } from 'react-redux';
import { toasterMsg } from '../../../../../Components/Toaster/Toaster';
import * as action from '../../Store/Pom.action';
import { checkArray, createOptions } from '../../../../../Components/helper';
import * as systemMasterAction from '../../../../SystemMasters/Store/SystemMasters.action';

export const StateContext = createContext(null);

export default function PomGroupProvider({ children }) {
  const dispatch = useDispatch();
  const { id } = useParams();

  const { t } = useTranslation('common');
  const history = useHistory();
  const reducerState = useSelector((redState) => redState);
  const {
    PomDetails: { CategoryOneList, CategoryTwoList, CategoryThreeList, pomList, pomGroupDataById, CategoryFourList },
    UserDetails: { userInfo },
    SystemMastersDetails: { pcTypeList },
  } = reducerState;
  const loggedInUserId = userInfo.userId;

  const initialState = {
    groupName: '',
    productTypeData: [],
    productTypeId: [],
    CategoryOneData: [],
    selectCategoryOne: [],
    CategoryTwoData: [],
    selectCategoryTwo: [],
    CategoryThreeData: [],
    selectCategoryThree: [],
    selectCategoryFour: [],
    categoryFourData: [],
    pomTableData: [],

    selectedPOM: [],

    showError: false,
    isisFocused: false,
    tableView: true,
    showImageUpload: false,
    image: '',
    fullImage: '',
    imageName: '',
    showDeleteModal: false,
    catBasedOnPTData: false,
  };
  const [pomGroupState, setPomGroupState] = useState({ ...initialState });

  useEffect(() => {
    if (!isEmpty(pomGroupDataById)) {
      // Normalize productTypeId to an array for the MultiSelect
      let hydratedPT = [];
      if (Array.isArray(pomGroupDataById?.productType)) {
        hydratedPT = pomGroupDataById.productType;
      } else if (Array.isArray(pomGroupDataById?.productTypeId)) {
        hydratedPT = pomGroupDataById.productTypeId;
      } else if (Array.isArray(pomGroupDataById?.productTypeIds)) {
        hydratedPT = pomGroupDataById.productTypeIds;
      } else if (pomGroupDataById?.productTypeId) {
        hydratedPT = [pomGroupDataById.productTypeId];
      }

      // Normalize Cat3/Cat4 ids to arrays for MultiSelects
      const hydratedC3 = Array.isArray(pomGroupDataById?.catThreeId)
        ? pomGroupDataById.catThreeId
        : pomGroupDataById?.catThreeId
        ? [pomGroupDataById.catThreeId]
        : [];
      const hydratedC4 = Array.isArray(pomGroupDataById?.catFourId)
        ? pomGroupDataById.catFourId
        : pomGroupDataById?.catFourId
        ? [pomGroupDataById.catFourId]
        : [];

      setPomGroupState((prev) => ({
        ...prev,
        groupName: pomGroupDataById?.groupName,
        // store selections as objects if present else IDs
        selectCategoryOne: Array.isArray(pomGroupDataById?.catsOneIds) ? pomGroupDataById.catsOneIds : [],
        selectCategoryTwo: Array.isArray(pomGroupDataById?.catsTwoIds) ? pomGroupDataById.catsTwoIds : [],
        selectCategoryThree: hydratedC3,
        selectCategoryFour: hydratedC4,
        pomTableData: pomGroupDataById?.pomLibraries,
        image: pomGroupDataById?.image || '',
        fullImage: pomGroupDataById?.fullImage || '',
        tableView: pomGroupDataById?.tableView,
        productTypeId: hydratedPT.length ? hydratedPT : prev.productTypeId,
      }));
    }
  }, [pomGroupDataById]);

  // Ensure dependent category lists are fetched immediately after edit hydration
  useEffect(() => {
    // Fetch Cat3 when Cat1 & Cat2 are hydrated
    const hasC1 = Array.isArray(pomGroupState.selectCategoryOne) && pomGroupState.selectCategoryOne.length;
    const hasC2 = Array.isArray(pomGroupState.selectCategoryTwo) && pomGroupState.selectCategoryTwo.length;
    if (hasC1 && hasC2) {
      const toIdArray = (val) => (Array.isArray(val) ? val.map((v) => (typeof v === 'object' ? v?._id : v)).filter(Boolean) : []);
      const params = {
        isActive: 1,
        loggedInUserId,
        categoryOneId: toIdArray(pomGroupState.selectCategoryOne),
        categoryTwoId: toIdArray(pomGroupState.selectCategoryTwo),
        categoryType: 3,
      };
      if (pomGroupState.catBasedOnPTData) {
        params.productTypeId = pomGroupState.productTypeId || [];
      }
      dispatch(action.fetchCategoryThreeForGrading(params));
    }
  }, [dispatch, loggedInUserId, pomGroupState.selectCategoryOne, pomGroupState.selectCategoryTwo, pomGroupState.catBasedOnPTData, pomGroupState.productTypeId]);

  useEffect(() => {
    // Fetch Cat4 when Cat1, Cat2 & Cat3 are hydrated
    const hasC1 = Array.isArray(pomGroupState.selectCategoryOne) && pomGroupState.selectCategoryOne.length;
    const hasC2 = Array.isArray(pomGroupState.selectCategoryTwo) && pomGroupState.selectCategoryTwo.length;
    const hasC3 = Array.isArray(pomGroupState.selectCategoryThree) && pomGroupState.selectCategoryThree.length;
    if (hasC1 && hasC2 && hasC3) {
      const toIdArray = (val) => (Array.isArray(val) ? val.map((v) => (typeof v === 'object' ? v?._id : v)).filter(Boolean) : []);
      const params = {
        isActive: 1,
        loggedInUserId,
        categoryOneId: toIdArray(pomGroupState.selectCategoryOne),
        categoryTwoId: toIdArray(pomGroupState.selectCategoryTwo),
        categoryThreeId: toIdArray(pomGroupState.selectCategoryThree),
        categoryType: 4,
      };
      if (pomGroupState.catBasedOnPTData) {
        params.productTypeId = pomGroupState.productTypeId || [];
      }
      dispatch(action.fetchCategoryFour(params));
    }
  }, [
    dispatch,
    loggedInUserId,
    pomGroupState.selectCategoryOne,
    pomGroupState.selectCategoryTwo,
    pomGroupState.selectCategoryThree,
    pomGroupState.catBasedOnPTData,
    pomGroupState.productTypeId,
  ]);
  useEffect(() => {
    try {
      dispatch(
        systemMasterAction.getPCTypesDetails({
          loggedInUserId: userInfo?.userId,
          isActive: true,
          tier: 'Tier1',
        }),
      );
    } catch (e) {
      // no-op
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setPomGroupState((prev) => ({
          ...prev,
          catBasedOnPTData: !!res,
        }));
      } catch (e) {
        setPomGroupState((prev) => ({
          ...prev,
          catBasedOnPTData: false,
        }));
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    const init = async () => {
      const queryParams = {
        isActive: 1,
        loggedInUserId,
      };
      await Promise.all([
        dispatch(action.fetchBrandsForGradingGroup(queryParams)),
        dispatch(action.fetchCategoryOneForGrading({ ...queryParams, categoryType: 1 })),
        dispatch(action.fetchCategoryTwoForGrading({ ...queryParams, categoryType: 2 })),
      ]);
    };
    init();
  }, [dispatch, loggedInUserId]);

  useEffect(() => {
    setPomGroupState((prev) => ({
      ...prev,
      productTypeData: createOptions(pcTypeList, 'name', '_id'),
    }));
  }, [pcTypeList]);

  useEffect(() => {
    setPomGroupState((prev) => ({
      ...prev,
      CategoryThreeData: createOptions(CategoryThreeList, 'name', '_id'),
    }));
  }, [CategoryThreeList]);

  useEffect(() => {
    setPomGroupState((prev) => ({
      ...prev,
      categoryFourData: createOptions(CategoryFourList, 'name', '_id'),
    }));
  }, [CategoryFourList]);

  // Gated cascading for Category 1-4: do NOT hide options before selection; only filter after parent is chosen
  useEffect(() => {
    if (!pomGroupState.catBasedOnPTData) {
      // Flag off: keep old lists unmodified
      setPomGroupState((prev) => ({
        ...prev,
        CategoryOneData: createOptions(CategoryOneList, 'name', '_id'),
        CategoryTwoData: createOptions(CategoryTwoList, 'name', '_id'),
        CategoryThreeData: createOptions(CategoryThreeList, 'name', '_id'),
        categoryFourData: createOptions(CategoryFourList, 'name', '_id'),
      }));
      return;
    }

    const toIdArray = (val) => (Array.isArray(val) ? val.map((v) => (typeof v === 'object' ? v?._id : v)).filter(Boolean) : []);
    const selectedPTIds = toIdArray(pomGroupState.productTypeId);
    const selectedC1Ids = toIdArray(pomGroupState.selectCategoryOne);
    const selectedC2Ids = toIdArray(pomGroupState.selectCategoryTwo);
    const selectedC3Ids = toIdArray(pomGroupState.selectCategoryThree);

    // Category 1: if no PT selected -> EMPTY; else filter by PT
    const cat1Base = CategoryOneList || [];
    const cat1Filtered = !selectedPTIds.length
      ? []
      : cat1Base.filter((c1) => {
          const pts = c1?.productType || [];
          const arr = Array.isArray(pts) ? pts : [pts];
          const ids = arr.map((x) => x?._id || x).filter(Boolean);
          return ids.some((id) => selectedPTIds.includes(id));
        });

    // Category 2: if no Cat1 selected -> EMPTY; else filter by Cat1
    const cat2Base = CategoryTwoList || [];
    const cat2Filtered = !selectedC1Ids.length
      ? []
      : cat2Base.filter((c2) => {
          const rel = c2?.categoryOneId || c2?.catOneIds || [];
          const arr = Array.isArray(rel) ? rel : [rel];
          const ids = arr.map((x) => x?._id || x).filter(Boolean);
          return ids.some((id) => selectedC1Ids.includes(id));
        });

    // Category 3: if Cat1 or Cat2 not selected -> EMPTY; else filter by Cat1 + Cat2
    const cat3Base = CategoryThreeList || [];
    const cat3Filtered =
      !selectedC1Ids.length || !selectedC2Ids.length
        ? []
        : cat3Base.filter((c3) => {
            const r1 = c3?.categoryOneId || c3?.catOneIds || [];
            const r2 = c3?.categoryTwoId || c3?.catTwoIds || [];
            const a1 = (Array.isArray(r1) ? r1 : [r1]).map((x) => x?._id || x).filter(Boolean);
            const a2 = (Array.isArray(r2) ? r2 : [r2]).map((x) => x?._id || x).filter(Boolean);
            return a1.some((id) => selectedC1Ids.includes(id)) && a2.some((id) => selectedC2Ids.includes(id));
          });

    // Category 4: if Cat1/Cat2/Cat3 not all selected -> EMPTY; else filter by Cat1 + Cat2 + Cat3
    const cat4Base = CategoryFourList || [];
    const cat4Filtered =
      !selectedC1Ids.length || !selectedC2Ids.length || !selectedC3Ids.length
        ? []
        : cat4Base.filter((c4) => {
            const r1 = c4?.categoryOneId || c4?.catOneIds || [];
            const r2 = c4?.categoryTwoId || c4?.catTwoIds || [];
            const r3 = c4?.categoryThreeId || c4?.catThreeIds || [];
            const a1 = (Array.isArray(r1) ? r1 : [r1]).map((x) => x?._id || x).filter(Boolean);
            const a2 = (Array.isArray(r2) ? r2 : [r2]).map((x) => x?._id || x).filter(Boolean);
            const a3 = (Array.isArray(r3) ? r3 : [r3]).map((x) => x?._id || x).filter(Boolean);
            return a1.some((id) => selectedC1Ids.includes(id)) && a2.some((id) => selectedC2Ids.includes(id)) && a3.some((id) => selectedC3Ids.includes(id));
          });

    setPomGroupState((prev) => ({
      ...prev,
      CategoryOneData: createOptions(cat1Filtered, 'name', '_id'),
      CategoryTwoData: createOptions(cat2Filtered, 'name', '_id'),
      CategoryThreeData: createOptions(cat3Filtered, 'name', '_id'),
      categoryFourData: createOptions(cat4Filtered, 'name', '_id'),
    }));
  }, [
    pomGroupState.catBasedOnPTData,
    pomGroupState.productTypeId,
    pomGroupState.selectCategoryOne,
    pomGroupState.selectCategoryTwo,
    pomGroupState.selectCategoryThree,
    CategoryOneList,
    CategoryTwoList,
    CategoryThreeList,
    CategoryFourList,
  ]);

  useEffect(() => {
    setPomGroupState((prev) => ({
      ...prev,

      CategoryOneData: createOptions(CategoryOneList, 'name', '_id'),
      CategoryTwoData: createOptions(CategoryTwoList, 'name', '_id'),
    }));
  }, [CategoryOneList, CategoryTwoList]);

  useEffect(() => {
    setPomGroupState((prev) => ({
      ...prev,
      libraryList: pomList,
    }));
  }, [pomList]);

  // Clear dependent selections when a parent is removed (flag ON only)
  useEffect(() => {
    if (!pomGroupState?.catBasedOnPTData) return;
    if (!pomGroupState?.productTypeId || pomGroupState.productTypeId.length === 0) {
      setPomGroupState((prev) => ({
        ...prev,
        selectCategoryOne: [],
        selectCategoryTwo: [],
        selectCategoryThree: [],
        selectCategoryFour: [],
      }));
    }
  }, [pomGroupState?.catBasedOnPTData, pomGroupState?.productTypeId]);

  useEffect(() => {
    if (!pomGroupState?.catBasedOnPTData) return;
    if (!pomGroupState?.selectCategoryOne || pomGroupState.selectCategoryOne.length === 0) {
      setPomGroupState((prev) => ({
        ...prev,
        selectCategoryTwo: [],
        selectCategoryThree: [],
        selectCategoryFour: [],
      }));
    }
  }, [pomGroupState?.catBasedOnPTData, pomGroupState?.selectCategoryOne]);

  useEffect(() => {
    if (!pomGroupState?.catBasedOnPTData) return;
    if (!pomGroupState?.selectCategoryTwo || pomGroupState.selectCategoryTwo.length === 0) {
      setPomGroupState((prev) => ({
        ...prev,
        selectCategoryThree: [],
        selectCategoryFour: [],
      }));
    }
  }, [pomGroupState?.catBasedOnPTData, pomGroupState?.selectCategoryTwo]);

  useEffect(() => {
    if (!pomGroupState?.catBasedOnPTData) return;
    if (!pomGroupState?.selectCategoryThree || pomGroupState.selectCategoryThree.length === 0) {
      setPomGroupState((prev) => ({
        ...prev,
        selectCategoryFour: [],
      }));
    }
  }, [pomGroupState?.catBasedOnPTData, pomGroupState?.selectCategoryThree]);

  const handleCheckPOMList = useCallback(
    async (isChecked, tempId) => {
      const existingIds = pomGroupState?.selectedPOM;
      if (isChecked && !existingIds.includes(tempId)) {
        setPomGroupState((prevState) => ({
          ...prevState,
          selectedPOM: [...existingIds, tempId],
        }));
      } else {
        const removedArray = existingIds?.filter((eleId) => eleId !== tempId);

        if (checkArray(removedArray)) {
          setPomGroupState((prevState) => ({
            ...prevState,
            selectedPOM: removedArray,
          }));
        } else {
          setPomGroupState((prevState) => ({
            ...prevState,
            selectedPOM: [],
          }));
        }
      }
    },
    [pomGroupState?.selectedPOM],
  );

  useEffect(() => {
    const fetchData = async () => {
      const obj = {
        section: 'libraries',
        module: 'pomGrading',
        action: 'read',
        fromDetails: !!pomGroupState?.catBasedOnPTData,
      };
      await dispatch(action.fetchPomGroupById(id, obj));
    };
    if (id) {
      fetchData();
    }
  }, [id, dispatch, pomGroupState?.catBasedOnPTData]);

  const isallChecked = checkArray(pomGroupState?.pomTableData) && pomGroupState?.selectedPOM?.length === pomGroupState?.pomTableData?.length;
  const handleCheckAll = useCallback(
    async (e) => {
      if (e.target.checked) {
        const allIds = pomGroupState?.pomTableData && checkArray(pomGroupState?.pomTableData) && pomGroupState?.pomTableData.map((ele) => ele._id);

        if (allIds) {
          setPomGroupState((prevState) => ({
            ...prevState,
            selectedPOM: allIds.filter((element) => element !== undefined),
          }));
        }
      } else {
        setPomGroupState((prevState) => ({
          ...prevState,
          selectedPOM: [],
        }));
      }
    },
    [pomGroupState, setPomGroupState],
  );

  const checkValidation = () => {
    if (!pomGroupState?.groupName && !pomGroupState?.pomTableData.length) {
      setPomGroupState((prevState) => ({
        ...prevState,
        showError: true,
      }));
      toasterMsg(t('POM_GROUP.POM_REQUIRED'), 'danger');
      return false;
    }
    if (!pomGroupState?.pomTableData.length) {
      toasterMsg(t('POM_GROUP.POM_REQUIRED'), 'danger');
      return false;
    }
    if (!pomGroupState?.groupName) {
      setPomGroupState((prevState) => ({
        ...prevState,
        showError: true,
      }));
      return false;
    }
    return true;
  };

  const handleSubmitPomGroup = useCallback(async () => {
    const validate = checkValidation();
    if (validate) {
      const toIdArray = (val) => (Array.isArray(val) ? val.map((v) => (typeof v === 'object' ? v?._id : v)).filter(Boolean) : []);
      const req = {
        catsOneIds: toIdArray(pomGroupState?.selectCategoryOne),
        catsTwoIds: toIdArray(pomGroupState?.selectCategoryTwo),
        catThreeId: toIdArray(pomGroupState?.selectCategoryThree),
        catFourId: toIdArray(pomGroupState?.selectCategoryFour),
        groupName: pomGroupState?.groupName,
        loggedInUserId,
        pomLibraries: JSON.stringify(pomGroupState?.pomTableData),
        pomGroupId: '',
        image: pomGroupState?.image,
        fullImage: pomGroupState?.fullImage,
        imageName: pomGroupState?.imageName,
        section: 'libraries',
        module: 'pomGrading',
        action: 'addEdit',
        tableView: pomGroupState?.tableView,
      };
      if (pomGroupState?.catBasedOnPTData) {
        req.productTypeId = toIdArray(pomGroupState?.productTypeId);
      }
      if (id) {
        req.pomGroupId = id;
      }
      const res = await dispatch(action.savePomGroup(req, t));
      if (res.code === 200 && !id) {
        setTimeout(() => {
          history.push({
            pathname: '/libraries/pom-groups',
          });
        }, 2000);
      }
    }
  }, [pomGroupState, id, loggedInUserId, t, history, dispatch]);

  const memoizedContextValue = useMemo(() => {
    return {
      pomGroupState,
      setPomGroupState,
      loggedInUserId,
      handleCheckPOMList,
      handleCheckAll,
      isallChecked,
      handleSubmitPomGroup,
    };
  }, [setPomGroupState, pomGroupState, loggedInUserId, handleCheckPOMList, handleCheckAll, isallChecked, handleSubmitPomGroup]);

  return <StateContext.Provider value={memoizedContextValue}>{children}</StateContext.Provider>;
}

export function usePOMGroupState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the StateProvider');
  }
  return context;
}
